import React from 'react';
import { Cookie, Settings, BarChart, Shield, Eye } from 'lucide-react';

export const CookiePolicy: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Cookie className="h-16 w-16 text-white mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Cookie Policy
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Learn about how we use cookies and similar technologies to enhance your browsing experience.
            </p>
            <p className="text-sm text-primary-200 mt-4">
              Last updated: January 1, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Are Cookies?</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Cookies are small text files that are stored on your device when you visit a website. They help 
                websites remember information about your visit, such as your preferred language and other settings, 
                which can make your next visit easier and the site more useful to you.
              </p>
              <p className="text-gray-600 leading-relaxed">
                This Cookie Policy explains how OutstandingNet uses cookies and similar technologies when you 
                visit our website.
              </p>
            </div>

            {/* Types of Cookies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Types of Cookies We Use</h2>
              
              {/* Essential Cookies */}
              <div className="card p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="h-6 w-6 text-accent-600 mr-3" />
                  Essential Cookies
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These cookies are necessary for the website to function properly. They enable basic functions 
                  like page navigation and access to secure areas of the website.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Session management</li>
                  <li>Security and authentication</li>
                  <li>Load balancing</li>
                  <li>Form submission</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  <strong>Duration:</strong> Session cookies (deleted when you close your browser)
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="card p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart className="h-6 w-6 text-primary-600 mr-3" />
                  Analytics Cookies
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These cookies help us understand how visitors interact with our website by collecting and 
                  reporting information anonymously.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Google Analytics (traffic analysis)</li>
                  <li>Page views and user behavior</li>
                  <li>Popular content identification</li>
                  <li>Website performance monitoring</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  <strong>Duration:</strong> Up to 2 years
                </p>
              </div>

              {/* Functional Cookies */}
              <div className="card p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Settings className="h-6 w-6 text-secondary-600 mr-3" />
                  Functional Cookies
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These cookies enable enhanced functionality and personalization, such as remembering your 
                  preferences and settings.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Language preferences</li>
                  <li>Theme and display settings</li>
                  <li>Form data retention</li>
                  <li>User interface customization</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  <strong>Duration:</strong> Up to 1 year
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="card p-6 mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <Eye className="h-6 w-6 text-yellow-600 mr-3" />
                  Marketing Cookies
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4">
                  These cookies are used to track visitors across websites to display relevant and engaging 
                  advertisements.
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Google Ads and remarketing</li>
                  <li>Social media integration</li>
                  <li>Conversion tracking</li>
                  <li>Personalized advertising</li>
                </ul>
                <p className="text-sm text-gray-500 mt-4">
                  <strong>Duration:</strong> Up to 2 years
                </p>
              </div>
            </div>

            {/* Third-Party Cookies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Third-Party Cookies</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may use third-party services that set their own cookies. These include:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Google Analytics</h4>
                  <p className="text-sm text-gray-600 mb-2">Website traffic analysis and user behavior tracking</p>
                  <a href="https://policies.google.com/privacy" className="text-primary-600 text-sm hover:underline">
                    Google Privacy Policy
                  </a>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Google Ads</h4>
                  <p className="text-sm text-gray-600 mb-2">Advertising and conversion tracking</p>
                  <a href="https://policies.google.com/technologies/ads" className="text-primary-600 text-sm hover:underline">
                    Google Ads Policy
                  </a>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Facebook Pixel</h4>
                  <p className="text-sm text-gray-600 mb-2">Social media advertising and analytics</p>
                  <a href="https://www.facebook.com/privacy/explanation" className="text-primary-600 text-sm hover:underline">
                    Facebook Privacy Policy
                  </a>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">LinkedIn Insights</h4>
                  <p className="text-sm text-gray-600 mb-2">Professional network analytics</p>
                  <a href="https://www.linkedin.com/legal/privacy-policy" className="text-primary-600 text-sm hover:underline">
                    LinkedIn Privacy Policy
                  </a>
                </div>
              </div>
            </div>

            {/* Managing Cookies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Managing Your Cookie Preferences</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Browser Settings</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Most web browsers allow you to control cookies through their settings. You can:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>Block all cookies</li>
                <li>Block third-party cookies only</li>
                <li>Delete existing cookies</li>
                <li>Set preferences for specific websites</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-4">Browser-Specific Instructions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Chrome</h4>
                  <p className="text-sm text-gray-600">Settings → Privacy and Security → Cookies and other site data</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Firefox</h4>
                  <p className="text-sm text-gray-600">Options → Privacy & Security → Cookies and Site Data</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Safari</h4>
                  <p className="text-sm text-gray-600">Preferences → Privacy → Manage Website Data</p>
                </div>
                
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Edge</h4>
                  <p className="text-sm text-gray-600">Settings → Cookies and site permissions → Cookies and site data</p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <p className="text-yellow-800 text-sm">
                  <strong>Note:</strong> Disabling certain cookies may affect the functionality of our website 
                  and your user experience.
                </p>
              </div>
            </div>

            {/* Opt-Out Options */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Opt-Out Options</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You can opt out of specific tracking services:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>
                  <a href="https://tools.google.com/dlpage/gaoptout" className="text-primary-600 hover:underline">
                    Google Analytics Opt-out Browser Add-on
                  </a>
                </li>
                <li>
                  <a href="https://www.google.com/settings/ads" className="text-primary-600 hover:underline">
                    Google Ads Settings
                  </a>
                </li>
                <li>
                  <a href="http://optout.networkadvertising.org/" className="text-primary-600 hover:underline">
                    Network Advertising Initiative Opt-out
                  </a>
                </li>
                <li>
                  <a href="http://optout.aboutads.info/" className="text-primary-600 hover:underline">
                    Digital Advertising Alliance Opt-out
                  </a>
                </li>
              </ul>
            </div>

            {/* Mobile Devices */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Mobile Devices</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Mobile devices may use advertising identifiers instead of cookies:
              </p>
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li><strong>iOS:</strong> Settings → Privacy → Apple Advertising → Limit Ad Tracking</li>
                <li><strong>Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization</li>
              </ul>
            </div>

            {/* Updates to Policy */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Updates to This Policy</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may update this Cookie Policy from time to time to reflect changes in our practices or 
                for other operational, legal, or regulatory reasons. Please check this page periodically 
                for updates.
              </p>
            </div>

            {/* Contact Information */}
            <div className="card p-8 bg-gray-50">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Questions About Cookies?</h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <span className="text-gray-700">📧 support@outstandingnet.com</span>
                </div>
                <div className="flex items-center">
                  <span className="text-gray-700">📞 (586) 248-4115</span>
                </div>
                <div className="flex items-start">
                  <div className="text-gray-700">
                    📍 11908 Cedarwood Dr<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;Shelby Township, MI 48315
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};